import React, { useEffect, useState, useCallback } from "react";
import { Button, Spin, Modal, message, notification } from "antd";
import { useNavigate } from "react-router-dom";
import {
  getPlans,
  getSubscriptionStatus,
  payOrRedirect,
  changeAutoPayment,
} from "./api_v2/subscription";
import "./ChangePlanPage.css";

const formatMonths = (months) => {
  if (months === 1) return "1 месяц";
  if (months >= 2 && months <= 4) return `${months} месяца`;
  return `${months} месяцев`;
};

function ChangePlanPage() {
  const [plans, setPlans] = useState([]);
  const [currentAP, setCurrentAP] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const navigate = useNavigate();

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [plansData, statusData] = await Promise.all([
        getPlans(),
        getSubscriptionStatus(),
      ]);

      if (!plansData.success || !statusData.success) {
        setError("Не удалось загрузить данные");
        return;
      }

      const subStatus = statusData.subscription_status;
      if (subStatus !== "active") {
        navigate("/subscription", { replace: true });
        return;
      }

      setPlans(plansData.plans);
      setCurrentAP(plansData.current_auto_payment);
      setSubscription(statusData.subscription);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate("/register", { replace: true });
        return;
      }
      setError("Ошибка загрузки. Попробуйте позже.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePayNow = async (plan) => {
    setActionLoading(true);
    try {
      const result = await payOrRedirect(plan.id);
      if (!result.success) {
        message.error(result.message || "Ошибка создания платежа");
      } else if (result.charged) {
        notification.success({
          message: "Списание запущено",
          description: "После подтверждения платежа подписка будет продлена автоматически.",
          placement: "bottomRight",
        });
        window.setTimeout(loadData, 2500);
      }
    } catch {
      message.error("Ошибка создания платежа. Попробуйте позже.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangeAP = async (plan) => {
    setActionLoading(true);
    try {
      const result = await changeAutoPayment(plan.id);
      if (result.success) {
        notification.success({
          message: "Автоплатёж изменён",
          description: `Следующее списание: ${plan.amount} ₽ за ${formatMonths(plan.months)}`,
          placement: "bottomRight",
        });
        setCurrentAP(result.new_plan
          ? {
              plan_id: result.new_plan.plan_id,
              amount: result.new_plan.amount,
              months: result.new_plan.months,
              next_payment_date: result.new_plan.next_payment_date,
              status: currentAP?.status || "pending",
            }
          : currentAP
        );
      } else {
        message.error(result.message || "Не удалось сменить автоплатёж");
      }
    } catch {
      message.error("Ошибка смены автоплатежа. Попробуйте позже.");
    } finally {
      setActionLoading(false);
      setConfirmModal(null);
    }
  };

  const openPayConfirm = (plan) => {
    const isCurrent = currentAP?.plan_id === plan.id;
    setConfirmModal({
      type: "pay",
      plan,
      title: isCurrent ? "Продлить подписку" : "Оплатить новый тариф",
      content: isCurrent
        ? `Сначала попробуем списать ${plan.amount} ₽ с привязанной карты. Если не получится, откроем обычную оплату. Время будет добавлено к текущей подписке.`
        : `Сначала попробуем списать ${plan.amount} ₽ с привязанной карты. Если не получится, откроем обычную оплату. Текущие дни сохранятся, автоплатёж переключится на новый тариф.`,
    });
  };

  const openChangeAPConfirm = (plan) => {
    const nextDate = currentAP?.next_payment_date
      ? new Date(currentAP.next_payment_date).toLocaleDateString()
      : "—";

    setConfirmModal({
      type: "change",
      plan,
      title: "Сменить автоплатёж",
      content: `Следующее автоматическое списание (${nextDate}) будет по тарифу «${plan.name || formatMonths(plan.months)}» — ${plan.amount} ₽. Текущая подписка не изменится.`,
    });
  };

  const endDate = subscription?.end
    ? new Date(subscription.end).toLocaleDateString()
    : "—";

  const remainingDays = subscription?.end
    ? Math.max(0, Math.ceil((new Date(subscription.end) - new Date()) / (1000 * 60 * 60 * 24)))
    : 0;

  const hasActiveAP = currentAP && ["pending", "retry", "processing"].includes(currentAP.status);

  if (loading) {
    return (
      <div className="change-plan-container">
        <Spin tip="Загрузка тарифов..." size="large">
          <div className="loading-placeholder" />
        </Spin>
      </div>
    );
  }

  if (error) {
    return (
      <div className="change-plan-container">
        <p className="change-plan-error">{error}</p>
        <Button onClick={loadData}>Повторить</Button>
        <Button type="link" onClick={() => navigate("/subscription")}>Назад</Button>
      </div>
    );
  }

  return (
    <div className="change-plan-container">
      <h1 className="change-plan-title">Управление тарифом</h1>

      {/* Блок текущей подписки */}
      <div className="change-plan-current">
        <div className="change-plan-current__status">
          Подписка активна до {endDate}
          {remainingDays > 0 && <span> ({remainingDays} дн.)</span>}
        </div>
        {hasActiveAP && (
          <div className="change-plan-current__auto">
            Автоплатёж: {currentAP.amount} &#8381; / {formatMonths(currentAP.months)}
            {currentAP.next_payment_date && (
              <span>
                {" "}— следующее списание{" "}
                {new Date(currentAP.next_payment_date).toLocaleDateString()}
              </span>
            )}
          </div>
        )}
        {!hasActiveAP && (
          <div className="change-plan-current__warning">
            Автоплатёж отключён. Оплатите подписку для автоматического продления.
          </div>
        )}
      </div>

      {/* Карточки тарифов */}
      <div className="change-plan-list">
        {plans.map((plan) => {
          const isCurrent = hasActiveAP && currentAP?.plan_id === plan.id;
          const perMonth = Math.round(plan.amount / plan.months);
          const baseMonthly = plans.find((p) => p.months === 1)?.amount;
          const savings = baseMonthly
            ? Math.max(0, baseMonthly * plan.months - plan.amount)
            : 0;

          return (
            <div
              key={plan.id}
              className={`change-plan-card ${isCurrent ? "change-plan-card--current" : ""}`}
            >
              <div className="change-plan-card__info">
                <div className="change-plan-card__title-row">
                  <h3 className="change-plan-card__title">
                    {plan.name || formatMonths(plan.months)}
                  </h3>
                  {isCurrent && (
                    <span className="change-plan-card__badge">Текущий</span>
                  )}
                </div>
                {plan.months > 1 && (
                  <p className="change-plan-card__per-month">{perMonth} &#8381;/мес</p>
                )}
                {savings > 0 && (
                  <p className="change-plan-card__savings">
                    Экономия {savings} &#8381;
                  </p>
                )}
              </div>

              <div className="change-plan-card__right">
                <div className="change-plan-card__price">{plan.amount} &#8381;</div>
                <div className="change-plan-card__actions">
                  <Button
                    type="primary"
                    size="small"
                    disabled={actionLoading}
                    onClick={() => openPayConfirm(plan)}
                  >
                    {isCurrent ? "Продлить" : "Оплатить"}
                  </Button>
                  {!isCurrent && hasActiveAP && (
                    <Button
                      size="small"
                      disabled={actionLoading}
                      onClick={() => openChangeAPConfirm(plan)}
                    >
                      Сменить автоплатёж
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Button
        className="change-plan-back"
        type="default"
        onClick={() => navigate("/subscription")}
      >
        Назад
      </Button>

      {/* Модальное окно подтверждения */}
      <Modal
        title={confirmModal?.title}
        open={!!confirmModal}
        onOk={() => {
          if (!confirmModal) return;
          if (confirmModal.type === "pay") handlePayNow(confirmModal.plan);
          else handleChangeAP(confirmModal.plan);
        }}
        onCancel={() => setConfirmModal(null)}
        okText="Подтвердить"
        cancelText="Отмена"
        confirmLoading={actionLoading}
      >
        <p>{confirmModal?.content}</p>
      </Modal>
    </div>
  );
}

export default ChangePlanPage;
